import React, { useCallback, useEffect, useMemo, useState } from "react";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { DashboardPageHeader } from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import { createTeamMember, fetchTeam, removeTeamMember } from "../teamApi";

const initialsOf = (name, email) => {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

function MemberRow({ member, isOwner, onRemove, removing, t, locale }) {
  const joined = member.joinedAt
    ? new Date(member.joinedAt).toLocaleDateString(locale === "de" ? "de-DE" : "en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <li className="flex flex-wrap items-center gap-4 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-5 py-4 transition-colors hover:border-outline-variant/60">
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-label-sm font-bold ${
          member.isOwner
            ? "bg-primary/10 text-primary"
            : "bg-secondary-container text-on-secondary-container"
        }`}
        aria-hidden
      >
        {initialsOf(member.fullName, member.email)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-body-md font-semibold text-on-surface">
            {member.fullName || member.email}
          </span>
          {member.isOwner ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 font-label-sm text-[11px] font-bold uppercase tracking-wide text-primary">
              <MaterialIcon name="workspace_premium" className="text-[13px]" />
              {t("team.ownerBadge")}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-surface-container px-2.5 py-0.5 font-label-sm text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
              {t("team.memberBadge")}
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate font-label-sm text-on-surface-variant">
          {member.email}
          {joined ? ` · ${t("team.joinedOn", { date: joined })}` : ""}
        </span>
      </span>

      {isOwner && !member.isOwner ? (
        <button
          type="button"
          onClick={() => onRemove(member)}
          disabled={removing}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 font-label-sm font-medium text-on-surface-variant transition-colors hover:bg-error-container/40 hover:text-error disabled:opacity-50"
        >
          <MaterialIcon name="person_remove" className="text-[17px]" />
          {t("team.remove")}
        </button>
      ) : null}
    </li>
  );
}

const ClientTeamPage = () => {
  const { t, locale } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [confirmMember, setConfirmMember] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchTeam());
      setError("");
    } catch (err) {
      setError(err?.data?.detail || t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return [data.owner, ...data.members].filter(Boolean);
  }, [data]);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 4000);
  };

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setFormError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!fullName.trim() || !email.trim() || !password) {
      setFormError(t("team.errorMissing"));
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await createTeamMember({
        fullName: fullName.trim(),
        email: email.trim(),
        password,
      });
      resetForm();
      setFormOpen(false);
      await load();
      showToast(t("team.created"));
    } catch (err) {
      const code = err?.data?.code;
      if (code === "email_taken") setFormError(t("team.errorEmailTaken"));
      else if (code === "weak_password") {
        setFormError(err?.data?.detail || t("team.errorWeakPassword"));
      } else setFormError(err?.data?.detail || t("team.errorCreate"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (member) => {
    setRemovingId(member.id);
    try {
      await removeTeamMember(member.id);
      setConfirmMember(null);
      await load();
      showToast(t("team.removed", { name: member.fullName || member.email }));
    } catch (err) {
      setError(err?.data?.detail || t("team.errorRemove"));
    } finally {
      setRemovingId(null);
    }
  };

  const isOwner = Boolean(data?.isOwner);

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("team.eyebrow")}
          title={t("team.title")}
          subtitle={t("team.subtitle")}
          actions={
            isOwner ? (
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setFormOpen((open) => !open);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-label-sm font-semibold text-on-primary transition hover:bg-primary/90"
              >
                <MaterialIcon name={formOpen ? "close" : "person_add"} className="text-[18px]" />
                {formOpen ? t("common.cancel") : t("team.addMember")}
              </button>
            ) : null
          }
        />

        {error ? (
          <p className="mb-6 rounded-xl bg-error-container px-4 py-3 font-label-sm text-on-error-container">
            {error}
          </p>
        ) : null}

        {isOwner && formOpen ? (
          <form
            onSubmit={handleSubmit}
            className="tonal-card mb-stack-lg rounded-2xl border border-outline-variant/30 p-stack-lg"
          >
            <h2 className="font-headline-md text-primary">{t("team.addTitle")}</h2>
            <p className="mt-1 max-w-2xl font-body-md text-on-surface-variant">
              {t("team.addHint")}
            </p>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <label className="block">
                <span className="font-label-sm text-on-surface-variant">
                  {t("team.fieldName")}
                </span>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("team.fieldNamePlaceholder")}
                  className="mt-1 w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-3 outline-none focus:ring-2 focus:ring-secondary"
                />
              </label>
              <label className="block">
                <span className="font-label-sm text-on-surface-variant">
                  {t("team.fieldEmail")}
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("team.fieldEmailPlaceholder")}
                  autoComplete="off"
                  className="mt-1 w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-3 outline-none focus:ring-2 focus:ring-secondary"
                />
              </label>
              <label className="block">
                <span className="font-label-sm text-on-surface-variant">
                  {t("team.fieldPassword")}
                </span>
                <span className="relative mt-1 block">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("team.fieldPasswordPlaceholder")}
                    autoComplete="new-password"
                    className="w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-secondary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={t("team.togglePassword")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-on-surface-variant transition hover:bg-surface-container"
                  >
                    <MaterialIcon
                      name={showPassword ? "visibility_off" : "visibility"}
                      className="text-[18px]"
                    />
                  </button>
                </span>
              </label>
            </div>

            {formError ? (
              <p className="mt-4 rounded-lg bg-error-container px-4 py-2.5 font-label-sm text-on-error-container">
                {formError}
              </p>
            ) : null}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-label-sm font-semibold text-on-primary transition hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? t("team.creating") : t("team.createCta")}
                <MaterialIcon name="arrow_forward" className="text-[17px]" />
              </button>
              <span className="font-label-sm text-on-surface-variant">
                {t("team.passwordShareHint")}
              </span>
            </div>
          </form>
        ) : null}

        <section>
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-headline-md text-primary">{t("team.membersTitle")}</h2>
            {data ? (
              <span className="font-label-sm text-on-surface-variant">
                {t("team.memberCount", { count: rows.length })}
              </span>
            ) : null}
          </div>

          {loading ? (
            <ul className="space-y-3" aria-hidden>
              {[1, 2].map((i) => (
                <li
                  key={i}
                  className="h-[76px] animate-pulse rounded-xl border border-outline-variant/20 bg-surface-container-low"
                />
              ))}
            </ul>
          ) : rows.length === 0 ? (
            <p className="font-body-md text-on-surface-variant">{t("team.empty")}</p>
          ) : (
            <ul className="space-y-3">
              {rows.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isOwner={isOwner}
                  removing={removingId === member.id}
                  onRemove={setConfirmMember}
                  t={t}
                  locale={locale}
                />
              ))}
            </ul>
          )}

          {!loading && !isOwner ? (
            <p className="mt-5 flex items-start gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3 font-label-sm text-on-surface-variant">
              <MaterialIcon name="info" className="mt-0.5 shrink-0 text-[16px]" />
              {t("team.memberViewNote")}
            </p>
          ) : null}
        </section>

        {confirmMember ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="absolute inset-0 bg-[#0f172a]/45"
              aria-label={t("common.close")}
              onClick={() => setConfirmMember(null)}
            />
            <div className="relative z-10 w-full max-w-md rounded-2xl bg-surface p-6 shadow-[0_20px_50px_rgba(15,23,42,0.2)]">
              <h3 className="font-headline-md text-on-surface">{t("team.removeTitle")}</h3>
              <p className="mt-2 font-body-md text-on-surface-variant">
                {t("team.removeConfirm", {
                  name: confirmMember.fullName || confirmMember.email,
                })}
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmMember(null)}
                  className="rounded-full bg-primary px-5 py-2.5 font-label-sm font-semibold text-on-primary"
                >
                  {t("team.keepMember")}
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(confirmMember)}
                  disabled={removingId === confirmMember.id}
                  className="rounded-full px-4 py-2.5 font-label-sm font-medium text-error hover:underline disabled:opacity-50"
                >
                  {removingId === confirmMember.id
                    ? t("team.removing")
                    : t("team.remove")}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {toast ? (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-on-surface px-5 py-3 font-label-sm text-surface shadow-lg">
            {toast}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ClientTeamPage;
